import React from "react";
import { Card, CardActionArea, Typography, Box, Chip } from "@mui/material";
import TopicIcon from "@mui/icons-material/Topic";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { format } from "date-fns";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";

const DetailsRow = ({ icon: Icon, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
    <Icon sx={{ color: "action.active", fontSize: 20, mr: 1 }} />
    <Typography variant='body2' color='text.secondary'>
      {value}
    </Typography>
  </Box>
);

const TaxComputationCard = ({
  details,
  onClick,
  onEdit,
  onDelete,
  canEditTaxComp,
  canDeleteTaxComp
}) => {
  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(details);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(details);
  };

  return (
    <Card
      sx={{
        height: 260,
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          borderColor: "primary.main",
          "& .action-buttons": {
            opacity: 1
          }
        }
      }}
    >
      <CardActionArea
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          height: "100%",
          p: 2
        }}
        onClick={onClick}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2.5 }}>
          <TopicIcon
            sx={{
              color: "primary.main",
              fontSize: 22,
              mr: 1.5
            }}
          />
          <Typography
            variant='subtitle1'
            sx={{
              fontWeight: 500,
              color: "text.primary",
              flexGrow: 1,
              letterSpacing: "-0.01em"
            }}
          >
            YA {details.yearOfAssessment}
          </Typography>

          {details.taxComputationType === "investment" && (
            <Chip
              label='Investment Holding'
              color='secondary'
              size='small'
              sx={{
                fontWeight: 500,
                bgcolor: "secondary.light",
                color: "secondary.main"
              }}
            />
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flexGrow: 1 }}>
          <DetailsRow
            icon={CalendarTodayIcon}
            value={`${format(
              new Date(details.financialYearStart),
              "d MMM yyyy"
            )} - ${format(new Date(details.financialYearEnd), "d MMM yyyy")}`}
          />

          <DetailsRow
            icon={DateRangeIcon}
            value={
              details.taxPeriodType === "single_ya"
                ? "Single YA Period"
                : "Cross-YA Period"
            }
          />

          <DetailsRow
            icon={AccountBalanceIcon}
            value={`${details.currency} ${
              details.currency !== "SGD"
                ? `(Rate: ${details.manual_currency_rate || details.currency_rate})`
                : ""
            }`}
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            mt: "auto",
            pt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Typography
            variant='caption'
            sx={{
              color: "text.disabled",
              fontSize: "0.75rem",
              fontWeight: 400
            }}
          >
            Click to view
          </Typography>
          <Box
            className='action-buttons'
            sx={{
              opacity: 0,
              transition: "opacity 0.2s ease-in-out",
              display: "flex",
              gap: 0.5
            }}
          >
            {canEditTaxComp && (
              <IconButtonWithTooltip
                icon={EditIcon}
                tooltipTitle='Edit'
                onClick={handleEdit}
                color='primary'
                size='small'
                sx={{
                  "&:hover": {
                    backgroundColor: "action.hover"
                  }
                }}
              />
            )}
            {canDeleteTaxComp && (
              <IconButtonWithTooltip
                icon={DeleteIcon}
                tooltipTitle='Delete'
                onClick={handleDelete}
                color='error'
                size='small'
                sx={{
                  "&:hover": {
                    backgroundColor: "error.light",
                    color: "error.main"
                  }
                }}
              />
            )}
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default TaxComputationCard;
